import { useTranslate } from "@/lib/i18n";
import { formatCountdown, isolate, type DispatchChip } from "@/modules/pos/lib/dispatchBoard";

export type Tone = "info" | "success" | "warning" | "danger";

const TONE: Record<DispatchChip["kind"], Tone> = {
  offered: "info",
  accepted: "info",
  pickedUp: "info",
  arrived: "info",
  delivered: "success",
  failed: "warning",
  problem: "danger",
  notAnswered: "warning",
  declined: "warning",
  returned: "warning",
  deliveryCancelled: "warning",
};

const TONE_CLASS: Record<Tone, string> = {
  info: "bg-[color:var(--info-100)] text-[color:var(--info)]",
  success: "bg-[color:var(--success-100)] text-[color:var(--success)]",
  warning: "bg-[color:var(--warning-100,var(--surface-alt))] text-[color:var(--warning)]",
  danger: "bg-[color:var(--danger-100)] text-[color:var(--danger)]",
};

/**
 * The delivery tile's driver line (driver backend B3 · P13): who has the bag
 * and where it is — offered with the time left on the server's clock,
 * accepted, on the way, arrived, delivered, a reported problem, or who did
 * not answer. Not interactive: it sits INSIDE the tile's button on the PAPER
 * floor, so it must never be a control of its own.
 */
/**
 * A chip's words (2026-10-20, shared with the bill head's badge): `full` is
 * the tile's sentence with the driver's name («قبِل ⟨name⟩ الطلب»); `short`
 * the state alone («تم القبول من المندوب»).
 */
export function useDispatchChipText() {
  const t = useTranslate();
  return (chip: DispatchChip): { full: string; short: string } => {
    // First-strong isolates: a driver named «سامي P13» or "Hassan 2" must not
    // swap places with the countdown beside it in a right-to-left sentence.
    const name = isolate(chip.driverName);
    const time = chip.kind === "offered" ? formatCountdown(chip.secondsLeft) : "";
    switch (chip.kind) {
      case "offered":
        return { full: t((m) => m.dispatch.chipOffered, { name, time }), short: t((m) => m.dispatch.billChipOffered, { time }) };
      case "accepted":
        return { full: t((m) => m.dispatch.chipAccepted, { name }), short: t((m) => m.dispatch.billChipAccepted) };
      case "pickedUp":
        return { full: t((m) => m.dispatch.chipPickedUp, { name }), short: t((m) => m.dispatch.billChipPickedUp) };
      case "arrived":
        return { full: t((m) => m.dispatch.chipArrived, { name }), short: t((m) => m.dispatch.billChipArrived) };
      case "delivered":
        return { full: t((m) => m.dispatch.chipDelivered, { name }), short: t((m) => m.dispatch.billChipDelivered) };
      case "failed":
        return { full: t((m) => m.dispatch.chipFailed, { name }), short: t((m) => m.dispatch.billChipFailed) };
      case "problem":
        return { full: t((m) => m.dispatch.chipProblem, { name }), short: t((m) => m.dispatch.billChipProblem) };
      case "notAnswered":
        return { full: t((m) => m.dispatch.chipNotAnswered, { name }), short: t((m) => m.dispatch.billChipNotAnswered) };
      case "returned":
        return { full: t((m) => m.dispatch.chipReturned, { name }), short: t((m) => m.dispatch.billChipReturned) };
      case "deliveryCancelled":
        return {
          full: t((m) => m.dispatch.chipDeliveryCancelled, { name }),
          short: t((m) => m.dispatch.billChipDeliveryCancelled),
        };
      case "declined":
        return { full: t((m) => m.dispatch.chipDeclined, { name }), short: t((m) => m.dispatch.billChipDeclined) };
    }
  };
}

/** The tone of a chip's state (the bill head's badge reads it too). */
export function dispatchChipTone(kind: DispatchChip["kind"]): Tone {
  return TONE[kind];
}

export function DispatchStatusChip({
  chip,
  testId,
}: {
  chip: DispatchChip;
  testId?: string;
}) {
  const label = useDispatchChipText()(chip).full;
  const tone = TONE[chip.kind];
  return (
    <span
      data-ui="dispatch-chip"
      data-tone={tone}
      data-kind={chip.kind}
      data-testid={testId}
      // The countdown changes every second; announce the state, not each tick.
      aria-live={chip.kind === "offered" ? "off" : "polite"}
      className={`inline-flex max-w-full items-center self-start rounded-full px-2 py-[2px] text-[length:var(--font-size-2xs)] font-extrabold tabular-nums ${TONE_CLASS[tone]}`}
    >
      <span className="min-w-0 truncate">{label}</span>
    </span>
  );
}
